import React, { useState } from 'react';
import styled from 'styled-components';
import { IconButton } from '@mui/material';
import moment from 'moment';
import { UploadFile } from '@mui/icons-material';
import type { BatchUploadedData } from './types';
import { useBatchUpdateVisa } from '../../graphql';
import { QUALIFICATION_PAGE_TEXT } from '../../lang';
import { AssessmentAccordion } from '../../../Assessment';
import {
  Alert,
  type AlertDetails,
  Dialog,
  Loading,
} from '../../../../components';
import { postRequest, type PostRequestData } from '../../../../utils';

export type BatchUploadDialogProps = {
  open: boolean;
  handleClose: () => void;
  refetchList: () => void;
};

const { BATCH_UPLOAD_TEXT } = QUALIFICATION_PAGE_TEXT;

export const BatchUploadDialog: React.FC<BatchUploadDialogProps> = props => {
  const uploadTipText = BATCH_UPLOAD_TEXT.UPLOAD_REMARK;
  const isParsed = false;
  const { open, handleClose, refetchList } = props;
  const [batchUploadedData, setBatchUploadedData] = useState<BatchUploadedData>(
    {},
  );
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  /**
   * TODO: Used a pseudo-input element, decide whether to replace
   */
  const handleUploadClick = (): void => {
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      fileInput.click();
    }
  };

  const batchUpdateVisa = useBatchUpdateVisa();

  const handleBatchUploadSave: () => Promise<void> = async () => {
    setLoading(true);
    const result = await batchUpdateVisa({
      variables: {
        input: {
          records: batchUploadedData.matched,
        },
      },
    });
    if (result.errors) {
      setOpenAlert(true);
      setAlertDetails({
        alertType: 'error',
        alertContent: 'Failed to save the batch upload.',
      });
    } else {
      setLoading(false);
      refetchList();
      handleBatchUploadClose();
    }
  };

  const handleBatchUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (e.target.files === null || e.target.files.length < 1) return;

    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('files', file);

    setLoading(true);

    const postRequestData: PostRequestData = {
      url: '/eadmin-operation/visa/batch-upload',
      data: formData,
      responseType: 'json',
    };
    try {
      const response = await postRequest(postRequestData);
      const batchData: BatchUploadedData = response?.data;
      setBatchUploadedData(batchData);
    } catch (error) {
      setOpenAlert(true);
      setAlertDetails({
        alertType: 'error',
        alertContent: 'Failed to resolve the file',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBatchUploadClose = (): void => {
    setBatchUploadedData({});
    handleClose();
  };

  const emptyUploadedData: boolean = JSON.stringify(batchUploadedData) === '{}';
  const { matched, nonMatched } = batchUploadedData;

  const resultsArr = [
    {
      title: QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT.RESOLVED_RESULT,
      result: matched,
    },
    {
      title: QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT.NON_MATCHED,
      result: nonMatched,
    },
  ];

  return (
    <ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <Dialog
          openDialog={open}
          dialogTitle={QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT.TITLE}
          dialogAction={
            emptyUploadedData ? undefined : QUALIFICATION_PAGE_TEXT.IMPORT
          }
          onDialogAction={handleBatchUploadSave}
          cancelAction={QUALIFICATION_PAGE_TEXT.CLOSE}
          onDialogClose={handleBatchUploadClose}
          width="800px"
        >
          <div className="px-4">
            <div className={isParsed ? 'hidden' : 'p-2 mb-4'}>
              <IconButton onClick={handleUploadClick}>
                <UploadFile />
                <input
                  type="file"
                  id="file-input"
                  style={{ display: 'none' }}
                  onChange={handleBatchUpload}
                />
              </IconButton>
              <span className="italic">{uploadTipText}</span>
            </div>
            {resultsArr.map((item, index) => {
              const { title, result } = item;
              return (
                <AssessmentAccordion
                  key={index}
                  title={title + ` (${result?.length})`}
                  titlecolor="#951D1D"
                  className={result ? '' : 'hidden'}
                  defaultExpanded
                  AssessDetails={
                    <div className="w-full px-2">
                      <div className="grid grid-cols-6 gap-1 justify-items-center font-semibold">
                        <div>
                          {
                            QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT
                              .APPLICATION_NO
                          }
                        </div>
                        <div>
                          {QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT.REF_NO}
                        </div>
                        <div>
                          {
                            QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT
                              .IMMD_OFFICER
                          }
                        </div>
                        <div>
                          {
                            QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT
                              .VISA_LABEL_APPROVAL_DATE
                          }
                        </div>
                        <div>
                          {
                            QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT
                              .APPLICATION_RECEIVED_DATE
                          }
                        </div>
                        <div>
                          {
                            QUALIFICATION_PAGE_TEXT.BATCH_UPLOAD_TEXT
                              .SUBMISSION_DATE_TO_IMMD
                          }
                        </div>
                      </div>
                      {result?.map((row, index) => {
                        const {
                          referenceNo,
                          applicationNo,
                          immdOfficerInCharge,
                          visaLabelApprovalDate,
                          applicationReceivedDate,
                          submissionDateToImmd,
                        } = row;
                        return (
                          <div
                            key={index}
                            className="grid grid-cols-6 gap-1 justify-items-center"
                          >
                            <div>{applicationNo}</div>
                            <div>{referenceNo}</div>
                            <div>{immdOfficerInCharge}</div>
                            <div>
                              {moment(visaLabelApprovalDate).format(
                                'DD/MM/YYYY',
                              )}
                            </div>
                            <div>
                              {moment(applicationReceivedDate).format(
                                'DD/MM/YYYY',
                              )}
                            </div>
                            <div>
                              {moment(submissionDateToImmd).format(
                                'DD/MM/YYYY',
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <hr className="mx-8 mt-4" />
                    </div>
                  }
                />
              );
            })}
          </div>
        </Dialog>
      )}
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  width: 100%;

  hr {
    background-image: linear-gradient(to right, #fff, #ccc, #fff);
  }
`;
